import React, { useState } from "react";
import {
  User,
  Building,
  CreditCard,
  FileText,
  Download,
  Edit,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  CheckCircle,
  AlertTriangle,
  Calendar,
  DollarSign,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, InputField } from "../components/ui";

const AccountSettings = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);

  const tabs = [
    { id: "profile", name: "Business Profile", icon: Building },
    { id: "payment", name: "Payment Methods", icon: CreditCard },
    { id: "history", name: "Filing History", icon: FileText },
    { id: "billing", name: "Billing & Receipts", icon: Download },
  ];

  // Mock data - would come from API
  const [profileData, setProfileData] = useState({
    businessName: "Acme Corp LLC",
    businessType: "LLC",
    federalTaxId: "12-3456789",
    address: "123 Business St",
    city: "San Francisco",
    state: "CA",
    zipCode: "94102",
    phone: "(555) 123-4567",
    email: "john@acmecorp.com",
    contactName: "John Smith",
    title: "CFO",
  });

  const paymentMethods = [
    {
      id: 1,
      type: "Visa",
      last4: "4242",
      expiryMonth: "12",
      expiryYear: "2025",
      isDefault: true,
    },
    {
      id: 2,
      type: "MasterCard",
      last4: "8888",
      expiryMonth: "08",
      expiryYear: "2024",
      isDefault: false,
    },
  ];

  const filingHistory = [
    {
      id: 1,
      taxYear: 2024,
      jurisdictions: ["California", "Texas", "New York"],
      status: "Filed",
      filedDate: "2024-01-15",
      totalAssets: 3,
      totalValue: "$12,450.00",
      cost: "$199.00",
    },
    {
      id: 2,
      taxYear: 2023,
      jurisdictions: ["California", "Texas"],
      status: "Filed",
      filedDate: "2023-02-28",
      totalAssets: 2,
      totalValue: "$8,900.00",
      cost: "$149.00",
    },
    {
      id: 3,
      taxYear: 2022,
      jurisdictions: ["California"],
      status: "Filed",
      filedDate: "2022-03-15",
      totalAssets: 1,
      totalValue: "$5,200.00",
      cost: "$99.00",
    },
  ];

  const billingHistory = [
    {
      id: 1,
      date: "2024-01-15",
      description: "Multi-Filing Plan - 2024 Tax Year",
      amount: "$199.00",
      status: "Paid",
      downloadUrl: "/receipts/2024-001.pdf",
    },
    {
      id: 2,
      date: "2023-02-28",
      description: "Multi-Filing Plan - 2023 Tax Year",
      amount: "$149.00",
      status: "Paid",
      downloadUrl: "/receipts/2023-001.pdf",
    },
    {
      id: 3,
      date: "2022-03-15",
      description: "Single Filing - 2022 Tax Year",
      amount: "$99.00",
      status: "Paid",
      downloadUrl: "/receipts/2022-001.pdf",
    },
  ];

  const handleProfileSave = () => {
    // Would save to API
    setIsEditingProfile(false);
  };

  const handleDeletePaymentMethod = (id) => {
    // Would delete via API
    console.log("Delete payment method:", id);
  };

  const handleSetDefaultPayment = (id) => {
    // Would update via API
    console.log("Set default payment method:", id);
  };

  const renderProfile = () => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Business Information</CardTitle>
        <Button
          variant={isEditingProfile ? "outline" : "ghost"}
          size="sm"
          onClick={() => setIsEditingProfile(!isEditingProfile)}
          className="gap-2"
        >
          {isEditingProfile ? (
            <>
              <X className="h-4 w-4" />
              Cancel
            </>
          ) : (
            <>
              <Edit className="h-4 w-4" />
              Edit
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField
            label="Business Name"
            value={profileData.businessName}
            onChange={(e) => setProfileData({ ...profileData, businessName: e.target.value })}
            disabled={!isEditingProfile}
          />
          <InputField
            label="Business Type"
            value={profileData.businessType}
            onChange={(e) => setProfileData({ ...profileData, businessType: e.target.value })}
            disabled={!isEditingProfile}
          />
          <InputField
            label="Federal Tax ID (EIN)"
            value={profileData.federalTaxId}
            onChange={(e) => setProfileData({ ...profileData, federalTaxId: e.target.value })}
            disabled={!isEditingProfile}
          />
          <InputField
            label="Phone Number"
            value={profileData.phone}
            onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
            disabled={!isEditingProfile}
          />
          <InputField
            label="Business Address"
            value={profileData.address}
            onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
            disabled={!isEditingProfile}
            className="md:col-span-2"
          />
          <InputField
            label="City"
            value={profileData.city}
            onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
            disabled={!isEditingProfile}
          />
          <InputField
            label="State"
            value={profileData.state}
            onChange={(e) => setProfileData({ ...profileData, state: e.target.value })}
            disabled={!isEditingProfile}
          />
          <InputField
            label="ZIP Code"
            value={profileData.zipCode}
            onChange={(e) => setProfileData({ ...profileData, zipCode: e.target.value })}
            disabled={!isEditingProfile}
          />
          <div></div>
          <InputField
            label="Primary Contact Name"
            value={profileData.contactName}
            onChange={(e) => setProfileData({ ...profileData, contactName: e.target.value })}
            disabled={!isEditingProfile}
          />
          <InputField
            label="Title"
            value={profileData.title}
            onChange={(e) => setProfileData({ ...profileData, title: e.target.value })}
            disabled={!isEditingProfile}
          />
          <InputField
            label="Email Address"
            value={profileData.email}
            onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
            disabled={!isEditingProfile}
            type="email"
          />
        </div>

        {isEditingProfile && (
          <div className="flex gap-3 mt-6 pt-6 border-t border-neutral-200">
            <Button onClick={handleProfileSave} className="gap-2">
              <Save className="h-4 w-4" />
              Save Changes
            </Button>
            <Button variant="outline" onClick={() => setIsEditingProfile(false)}>
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderPaymentMethods = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Payment Methods</CardTitle>
          <Button onClick={() => setShowAddPayment(true)} className="gap-2" size="sm">
            <Plus className="h-4 w-4" />
            Add Payment Method
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-8 bg-neutral-100 rounded flex items-center justify-center">
                    <CreditCard className="h-4 w-4 text-neutral-600" />
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">
                      {method.type} ending in {method.last4}
                    </p>
                    <p className="text-sm text-neutral-500">
                      Expires {method.expiryMonth}/{method.expiryYear}
                      {method.isDefault && (
                        <span className="ml-2 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">
                          Default
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!method.isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetDefaultPayment(method.id)}
                    >
                      Set Default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeletePaymentMethod(method.id)}
                    className="text-error hover:text-error"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {showAddPayment && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Add Payment Method</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowAddPayment(false)}>
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Card Number"
                placeholder="1234 5678 9012 3456"
                className="md:col-span-2"
              />
              <InputField label="Expiry Date" placeholder="MM/YY" />
              <InputField label="CVV" placeholder="123" />
              <InputField
                label="Cardholder Name"
                placeholder="John Doe"
                className="md:col-span-2"
              />
            </div>
            <div className="flex gap-3 mt-6 pt-6 border-t border-neutral-200">
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Add Payment Method
              </Button>
              <Button variant="outline" onClick={() => setShowAddPayment(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderFilingHistory = () => (
    <Card>
      <CardHeader>
        <CardTitle>Filing History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Tax Year</th>
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Jurisdictions</th>
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Assets</th>
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Total Value</th>
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Filed Date</th>
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Status</th>
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filingHistory.map((filing) => (
                <tr key={filing.id} className="border-b border-neutral-100">
                  <td className="py-3 px-4 font-medium text-neutral-900">{filing.taxYear}</td>
                  <td className="py-3 px-4">
                    <div className="text-sm text-neutral-600">
                      {filing.jurisdictions.slice(0, 2).join(", ")}
                      {filing.jurisdictions.length > 2 && (
                        <span className="text-neutral-400">
                          +{filing.jurisdictions.length - 2} more
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-neutral-600">{filing.totalAssets}</td>
                  <td className="py-3 px-4 text-neutral-600">{filing.totalValue}</td>
                  <td className="py-3 px-4 text-neutral-600">{filing.filedDate}</td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                      <CheckCircle className="h-3 w-3" />
                      {filing.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <Button variant="ghost" size="sm" className="gap-2">
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  const renderBilling = () => (
    <Card>
      <CardHeader>
        <CardTitle>Billing History & Receipts</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Date</th>
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Description</th>
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Amount</th>
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Status</th>
                <th className="text-left py-3 px-4 font-medium text-neutral-900">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {billingHistory.map((bill) => (
                <tr key={bill.id} className="border-b border-neutral-100">
                  <td className="py-3 px-4 text-neutral-600">{bill.date}</td>
                  <td className="py-3 px-4 text-neutral-900">{bill.description}</td>
                  <td className="py-3 px-4 font-medium text-neutral-900">{bill.amount}</td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                      <CheckCircle className="h-3 w-3" />
                      {bill.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <Button variant="ghost" size="sm" className="gap-2">
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-neutral-900">Account Settings</h1>
            <p className="text-neutral-600 mt-1">
              Manage your business profile, payment methods, and filing history
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-6">
                <nav className="space-y-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                        activeTab === tab.id
                          ? "bg-primary text-white"
                          : "hover:bg-neutral-100 text-neutral-700"
                      }`}
                    >
                      <tab.icon className="h-4 w-4" />
                      {tab.name}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === "profile" && renderProfile()}
            {activeTab === "payment" && renderPaymentMethods()}
            {activeTab === "history" && renderFilingHistory()}
            {activeTab === "billing" && renderBilling()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
